import type { BarcodePort } from './types';

/**
 * Barcode scanning that works on the phones the fleet actually carries.
 *
 * The native `BarcodeDetector` API exists in Chrome on Android and NOWHERE on
 * iOS Safari. The previous version keyed `supported` off that API, so every
 * iPhone silently fell through to a text box — the driver was asked to type a
 * 12-character specimen ID at each stop, which in practice means the scan gets
 * skipped. That is the whole feature lost on half the fleet, with no error to
 * explain it.
 *
 * So: use the native detector where it exists, and a WASM decoder elsewhere.
 * The decoder is BUNDLED, not fetched from a CDN, because a driver in a hospital
 * basement has no network and the app has to work anyway.
 *
 * Capacitor swap: ML Kit barcode plugin (faster still, and no WASM download).
 */

interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats?: string[] }) => BarcodeDetectorLike;
  }
}

/*
 * Read whatever is put in front of the camera.
 *
 * This list was briefly cut to three — code_128, code_39, itf — on the argument
 * that the totes carry numeric stickers so the retail formats were dead weight.
 * Within an hour the owner could not scan a box of donuts or a bottle of juice,
 * and asked the right question: what are we actually gaining?
 *
 * Nothing that was ever measured. The scanning speed problem was the decoder
 * IDLING — a 160ms pause after each attempt, so the phone rested about two
 * thirds of the time. Fixing that (now 40ms, see below) is where the real
 * improvement came from, and it applies to every format equally. Trimming the
 * format list was a guess dressed up as an optimisation, and its only measured
 * effect was to stop the person testing the app from being able to test it.
 *
 * So: the original five, plus ITF.
 *
 *   code_128, code_39  - the usual encodings for numeric asset stickers
 *   itf                - Interleaved 2 of 5. Numeric, even-length; a six-digit
 *                        sticker fits exactly. This one is a real ADDITION: if
 *                        any of the odd stickers are ITF they could not be
 *                        decoded at all before, which would explain a driver
 *                        typing the number in instead.
 *   ean_13             - retail. Also how UPC-A arrives: `0034000473687` is a
 *                        12-digit UPC read as 13 with a leading zero, which is
 *                        why upc_a does not need listing separately.
 *   qr_code, data_matrix - no evidence of either in real use, but "no evidence"
 *                        was the same reasoning that removed ean_13, and being
 *                        wrong here means a label silently stops working in a
 *                        hospital. Not a trade worth making for unmeasured
 *                        milliseconds.
 *
 * If scanning ever needs to be genuinely faster, measure first: time the decode
 * on a real phone with a real sticker, then decide. Do not trim this list again
 * on reasoning alone.
 */
const FORMATS = [
  'code_128', 'code_39', 'itf', 'ean_13', 'qr_code', 'data_matrix',
];

/** Native first; otherwise load the polyfill once, on demand. */
let detectorPromise: Promise<BarcodeDetectorLike> | null = null;

/*
 * Is the decoder the phone's own, or WebAssembly we brought with us?
 *
 * This is the ONE capability the app branches on, and it is measured, not
 * assumed: the same code that Android absorbs kills an iPhone, because on
 * Android `detect()` is hardware and on iOS it is zxing compiled to Wasm doing
 * the work on the CPU. Read it as a capability, never as "is this an iPhone" --
 * the day Apple ships BarcodeDetector this flips by itself.
 */
/** The single scratch canvas the decoder draws into. See scanFromVideo. */
let scratch: HTMLCanvasElement | null = null;

/* ---------------------------------------------------------------------------
 * THE PHONE TUNES ITSELF. Nobody guesses a number here.
 *
 * The rest between attempts was briefly set from the platform: 200ms if the
 * decoder is WebAssembly, 40ms if it is hardware. The owner caught the flaw
 * before it shipped to anybody but him -- that number came from HIS iPhone 6,
 * and it would have been applied to every iPhone in the fleet, including an
 * iPhone Air and a 15 Pro Max that decode several times faster. Four drivers
 * would have got a needlessly sluggish scanner to protect one old handset, and
 * none of them would have said so, because they never do.
 *
 * Any fixed number has that defect. It is right for exactly one phone and
 * wrong for the rest, and the fleet will keep changing.
 *
 * So the decode is TIMED, and the rest matches it. Resting for as long as the
 * last decode took holds the phone at roughly half duty -- enough headroom that
 * WebKit is not driven to reclaim the tab, on any device, without ever asking
 * what device it is. A fast phone decodes in 30ms and rests 40ms: twelve
 * attempts a second. A tired one decodes in 250ms and rests 250ms: two
 * attempts a second, and it survives the route.
 *
 * Same for how much picture the decoder is handed. A phone that is keeping up
 * gets the full 640; one that is struggling gets 480, which is 44% less work
 * and still perfectly legible for a six-digit sticker.
 * ------------------------------------------------------------------------- */

/** Rolling average of what a decode actually costs on THIS phone, in ms. */
let decodeMs = 0;

function observeDecode(ms: number): void {
  // Exponential average: settles in a handful of frames, and one slow frame
  // caused by something else on the phone cannot swing it.
  decodeMs = decodeMs === 0 ? ms : decodeMs * 0.8 + ms * 0.2;
}

/** How long to rest before trying again. */
function restMs(): number {
  // Before the first decode there is nothing to go on, so start gently on a
  // software decoder and immediately let measurement take over.
  if (decodeMs === 0) return usesSoftwareDecoder() ? 120 : 40;
  return Math.min(250, Math.max(40, Math.round(decodeMs)));
}

/**
 * What this phone turned out to be, in one short string.
 *
 * Reported with the breadcrumbs so the self-tuning can be CHECKED rather than
 * believed. If the iPhone Air comes back "38ms@640" and the iPhone 6
 * "260ms@480", the mechanism is doing its job and we can see it doing it.
 */
export function decodeProfile(): string {
  if (decodeMs === 0) return 'decode:none';
  return `decode:${Math.round(decodeMs)}ms@${scanWidth()}`;
}

/** How wide a frame this phone has earned. */
function scanWidth(): number {
  return decodeMs > 160 ? 480 : 640;
}

export function usesSoftwareDecoder(): boolean {
  return typeof window !== 'undefined' && !window.BarcodeDetector;
}

function getDetector(): Promise<BarcodeDetectorLike> {
  detectorPromise ??= (async () => {
    if (window.BarcodeDetector) {
      return new window.BarcodeDetector({ formats: FORMATS });
    }
    // Dynamic import keeps the WASM decoder out of the initial bundle, so the
    // login and route screens stay fast for the Android half of the fleet that
    // never needs it.
    const { BarcodeDetector, prepareZXingModule, setZXingModuleOverrides } =
      await import('barcode-detector/pure');

    /*
     * The decoder fetches its own WebAssembly binary, and by default it takes it
     * from fastly.jsdelivr.net. Unacceptable here on three counts: a driver in a
     * hospital basement has no network, that host is not in our
     * content-security-policy, and it would put a third party in the path of
     * every scan. It is served from our own origin instead, precached by the
     * service worker, so the first scan works offline too.
     *
     * Set through BOTH APIs deliberately. `prepareZXingModule` is the current
     * one and `fireImmediately` makes a failure surface HERE, where it can be
     * reported, rather than inside a decode call whose errors were being
     * swallowed as "no barcode in view". `setZXingModuleOverrides` is kept for
     * older builds of the library. Neither is expensive and the cost of the
     * override silently not applying was a scanner that never worked on iPhones.
     */
    const overrides = { locateFile: () => '/wasm/zxing_reader.wasm' };
    setZXingModuleOverrides?.(overrides);
    await prepareZXingModule?.({ overrides, fireImmediately: true });
    // The polyfill types its format list as a closed union; FORMATS holds the
    // same strings, so this is a naming difference rather than a real mismatch.
    return new BarcodeDetector(
      { formats: FORMATS } as unknown as ConstructorParameters<typeof BarcodeDetector>[0],
    ) as unknown as BarcodeDetectorLike;
  })();
  return detectorPromise;
}

export const barcode: BarcodePort = {
  // Every phone can scan now: either natively or through the bundled decoder.
  // Manual entry stays available, but it is no longer the only option on iOS.
  supported: typeof window !== 'undefined',

  /*
   * Ask the decoder to exist, and let the failure be a failure.
   *
   * Called before the viewfinder goes live. Native BarcodeDetector resolves
   * instantly; the WASM path either loads or throws, and throwing is the
   * useful answer — it is what lets the screen say the scanner is broken on
   * this phone instead of showing a camera that can never read anything.
   */
  async ready(): Promise<void> {
    await getDetector();
  },

  async scanFromVideo(video: HTMLVideoElement, signal: AbortSignal): Promise<string> {
    const detector = await getDetector();

    /*
     * Decode a SMALL COPY of the middle of the frame, not the frame itself.
     *
     * The decoder was being handed a full 1280-wide video several times a
     * second. On the phones this fleet actually carries that is enough to make
     * Safari discard the tab and reload the app mid-scan. A barcode is legible
     * at 640 wide, and the label is always in the middle of the viewfinder, so
     * this does roughly a sixth of the work for the same result.
     */
    // Both of these are measured, not chosen. See the block above.
    /*
     * ONE scratch canvas for the whole app. This used to be created per call,
     * and the caller calls this once per barcode AND once per rejected
     * duplicate -- so a six-tote pickup built dozens of willReadFrequently
     * canvases, which on an old iPhone is a real cost for no benefit. There is
     * only ever one scan in flight.
     */
    const canvas = scratch ??= document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    return new Promise<string>((resolve, reject) => {
      /*
       * The GAP between attempts, not the rate of them.
       *
       * This was 260ms, then 160ms, and the owner still reported scanning as
       * slow. The reason is that the number was doing the wrong job: a decode on
       * an iPhone takes something like 40-80ms, and then this timer sat idle for
       * another 160ms before trying again. The phone was resting roughly two
       * thirds of the time while someone held a tote in front of it.
       *
       * 40ms, with the `busy` guard and the fact that the next attempt is only
       * scheduled after the previous one RETURNS, means the decoder runs nearly
       * continuously without ever queueing work on itself. The thing that was
       * actually killing the tab — decoding a full 1280-wide frame — was fixed
       * by the crop above and is unaffected by this.
       */
      // The rest between attempts is measured per frame -- see restMs().
      let timer: ReturnType<typeof setTimeout> | null = null;
      let busy = false;
      let settled = false;

      const stop = () => { if (timer !== null) clearTimeout(timer); timer = null; };

      /*
       * THE CRASH ON IPHONES WAS HERE, AND IT WAS A PROMISE THAT NEVER SETTLED.
       *
       * Aborting used to call stop() and nothing else. stop() clears the timer,
       * so no further tick ever ran -- and the ONLY code that settled this
       * promise lived inside tick. So closing the scanner left this promise
       * pending forever, and with it the closure holding the video element, the
       * camera stream, this canvas and the WASM decoder.
       *
       * The caller in BarcodeScanner.tsx awaits it in a loop, so that whole
       * async function stayed suspended too. One leak per open-and-close of the
       * scanner, and a pickup like CAPS is opened and closed once per tote.
       *
       * Android has a large per-tab memory budget and absorbed it. iOS WebKit
       * does not: it discards the tab, reloads, hits the same wall, and after a
       * few rounds Safari gives up with "A problem repeatedly occurred". That is
       * the screenshot from the Pickup stop at CAPS.
       *
       * Everything now goes through finish(), which settles exactly once and
       * always releases the listener.
       */
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        stop();
        signal.removeEventListener('abort', onAbort);
        fn();
      };

      function onAbort() {
        finish(() => reject(new Error('barcode_cancelled')));
      }

      const tick = async () => {
        if (settled) return;
        if (signal.aborted) { onAbort(); return; }
        if (!busy && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          busy = true;
          try {
            // The middle band of the picture, scaled down. Falls back to the
            // video element itself if a canvas is unavailable for any reason.
            let source: CanvasImageSource = video;
            const vw = video.videoWidth;
            const vh = video.videoHeight;
            if (ctx && vw > 0 && vh > 0) {
              const cropH = Math.round(vh * 0.5);        // middle half
              const cropY = Math.round((vh - cropH) / 2);
              const scale = Math.min(1, scanWidth() / vw);
              const cw = Math.round(vw * scale);
              const ch = Math.round(cropH * scale);
              /*
               * Assigned EVERY frame, and that is deliberate even though the
               * value rarely changes.
               *
               * It was briefly changed to only assign on a real size change, on
               * the reasoning that reallocating a backing store 25 times a
               * second is waste. It is waste on a desktop. On an old iPhone the
               * reassignment is also what RELEASES the previous buffer, and
               * without it a willReadFrequently canvas grows until WebKit
               * destroys the tab -- which the owner then hit four times in one
               * test route, recorded as four 'died' reports with `scan` as the
               * last thing that happened.
               *
               * Do not 'optimise' this again without testing on the oldest
               * phone in the fleet.
               */
              canvas.width = cw;
              canvas.height = ch;
              ctx.drawImage(video, 0, cropY, vw, cropH, 0, 0, cw, ch);
              source = canvas;
            }
            const startedAt = performance.now();
            const found = await detector.detect(source);
            observeDecode(performance.now() - startedAt);
            // The scanner may have been closed while that decode was running.
            if (settled || signal.aborted) { onAbort(); return; }
            const value = found[0]?.rawValue;
            if (value) { finish(() => resolve(value)); return; }
          } catch { /* transient decode errors: keep scanning */ }
          busy = false;
        }
        if (settled) return;
        timer = setTimeout(() => { void tick(); }, restMs());
      };

      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) { onAbort(); return; }   // already closed
      /*
       * Scheduled, never called straight away. The caller runs this in a loop
       * and skips duplicates with `continue`, so if this promise could settle
       * without the event loop turning, that loop would spin on microtasks --
       * which starves rendering and input completely. That is a frozen
       * viewfinder with a live camera and buttons that do nothing, which is
       * exactly what was reported.
       */
      timer = setTimeout(() => { void tick(); }, 0);
    });
  },
};

/**
 * Read a barcode from a still picture. Used only by the nightly engine check
 * (/engine-check, 15 Oct): the same decoder the scanner uses, fed a known
 * sticker, so a browser update that breaks scanning shows up overnight.
 */
export async function detectBarcodeInImage(img: HTMLImageElement): Promise<string> {
  const detector = await getDetector();
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  c.getContext('2d')?.drawImage(img, 0, 0);
  const found = await detector.detect(c);
  return found[0]?.rawValue ?? '';
}
